import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CheckCircle2 } from "lucide-react";

const FollowUpRow = ({ fu, onToggle }) => {
  const overdue = fu.status === "pending" && fu.due_date < new Date().toISOString().slice(0, 10);
  return (
    <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-[color:var(--hs-bg-1)] px-4 py-3">
      <button
        onClick={() => onToggle(fu)}
        data-testid={`followup-toggle-${fu.id}`}
        className={`shrink-0 ${fu.status === "done" ? "text-[#78ffb4]" : "text-white/30 hover:text-[color:var(--hs-lime)]"}`}
        aria-label="Toggle done"
      >
        <CheckCircle2 className="h-5 w-5" />
      </button>
      <div className="min-w-0 flex-1">
        <p className={`text-sm ${fu.status === "done" ? "text-white/40 line-through" : "text-white/90"}`}>{fu.title}</p>
        <p className="text-xs text-white/40">{fu.lead_name}{fu.notes ? ` · ${fu.notes}` : ""}</p>
      </div>
      <span className={`font-mono-ui text-xs shrink-0 ${overdue ? "text-[#ff7878]" : "text-[color:var(--hs-lime)]"}`}>
        {overdue ? "Overdue · " : ""}{fu.due_date}
      </span>
    </div>
  );
};

export const FollowUpsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/followups");
      setItems(data.items);
    } catch (e) {
      toast.error("Could not load follow-ups");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (fu) => {
    try {
      await api.patch(`/followups/${fu.id}`, { status: fu.status === "done" ? "pending" : "done" });
      load();
    } catch (e) {
      toast.error("Could not update follow-up");
    }
  };

  const pending = items.filter((f) => f.status === "pending");
  const done = items.filter((f) => f.status === "done");

  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-14 rounded-xl bg-white/5" />
        ))}
      </div>
    );
  }

  return (
    <div data-testid="crm-followups-tab">
      <Tabs defaultValue="pending">
        <TabsList className="bg-[color:var(--hs-bg-1)] border border-white/10 rounded-xl h-10 p-1 mb-4">
          <TabsTrigger value="pending" data-testid="followups-pending-tab" className="rounded-lg data-[state=active]:bg-[color:var(--hs-lime)] data-[state=active]:text-[color:var(--hs-bg-0)] text-white/60 text-xs px-4">
            Pending ({pending.length})
          </TabsTrigger>
          <TabsTrigger value="done" data-testid="followups-done-tab" className="rounded-lg data-[state=active]:bg-[color:var(--hs-lime)] data-[state=active]:text-[color:var(--hs-bg-0)] text-white/60 text-xs px-4">
            Completed ({done.length})
          </TabsTrigger>
        </TabsList>
        <TabsContent value="pending" className="space-y-2">
          {pending.length === 0 && (
            <p className="text-sm text-white/40 py-8 text-center">No pending follow-ups. Schedule them from any lead's detail panel.</p>
          )}
          {pending.map((fu) => (
            <FollowUpRow key={fu.id} fu={fu} onToggle={toggle} />
          ))}
        </TabsContent>
        <TabsContent value="done" className="space-y-2">
          {done.length === 0 && <p className="text-sm text-white/40 py-8 text-center">Nothing completed yet.</p>}
          {done.map((fu) => (
            <FollowUpRow key={fu.id} fu={fu} onToggle={toggle} />
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
};
